import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateSchedulingCategoryDto,
  PostingSchedule,
  UpdatePostingScheduleDto,
  UpdateSchedulingCategoryDto,
} from '@mg/shared';
import { actionErrorText, api, ApiRequestError } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Field';
import { useDialogs } from '../../components/ui/DialogProvider';
import { ErrorState, LoadingState } from '../../components/ui/States';
import { invalidateCategoryViews } from '../categories/invalidateCategoryViews';
import { SchedulePanel } from '../scheduling/SchedulePanel';
import { ScheduleSidebar, shownCategories, tabKey } from '../scheduling/ScheduleSidebar';
import {
  addCategory,
  addSlot,
  enabledCount,
  hydrateDraft,
  patchSlot,
  removeCategory,
  removeSlot,
  renameCategory,
  slotsOf,
  toDto,
  type DraftCategory,
  type ScheduleDraft,
  type ScheduleId,
} from '../scheduling/scheduleDraft';

/** A small curated timezone list (the current value is always ensured present). */
const COMMON_TZ = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

/** What deleting a category does, said before it is done. */
function deleteMessage(postCount: number): string {
  if (postCount === 0) return 'No posts use this category. Its posting times are deleted.';
  if (postCount === 1) {
    return "1 post uses this category. It keeps its scheduled time and uses General from now on. The category's posting times are deleted.";
  }
  return `${postCount} posts use this category. They keep their scheduled times and use General from now on. The category's posting times are deleted.`;
}

/**
 * Scheduler Settings: the workspace's posting schedules. It is the page body of Campaigns & Posts at
 * `?view=scheduler`, under that page's own heading, which keys it by workspace (its draft must never
 * cross into another one). The queue fills these weekly times: Suggested Slots, Next Available,
 * Prioritize and Pick a Month draw a post's times from its scheduling category's schedule, or from
 * General, the catch-all for posts without one. Workspace admins edit; everyone else views.
 *
 * Every schedule lives in one draft, and "Save schedule" writes them all in one request. A category
 * itself is added, renamed or deleted at once, through its own endpoint, and patched into the draft:
 * that never throws away unsaved times of any schedule.
 */
export function PostingScheduleView() {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const isAdmin = useIsWorkspaceAdmin();
  const readOnly = !isAdmin;
  const { confirm } = useDialogs();

  const scheduleQ = useQuery({
    queryKey: ['posting-schedule', tenantKey],
    queryFn: ({ signal }) => api.getPostingSchedule(signal),
    enabled: tenantKey !== '',
  });

  const [draft, setDraft] = useState<ScheduleDraft | null>(null);
  const [selected, setSelected] = useState<ScheduleId>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Hydrate the editable form once from the server. A category change refreshes this query, and a
  // later answer never overwrites what is being edited.
  useEffect(() => {
    if (draft || !scheduleQ.data) return;
    setDraft(hydrateDraft(scheduleQ.data));
  }, [scheduleQ.data, draft]);

  const timezone = draft?.timezone ?? '';
  const tzOptions = useMemo(
    () => (timezone && !COMMON_TZ.includes(timezone) ? [timezone, ...COMMON_TZ] : COMMON_TZ),
    [timezone],
  );

  const save = useMutation({
    mutationFn: (dto: UpdatePostingScheduleDto) => api.updatePostingSchedule(dto),
    // The error line is an alert, read out when it appears: a save that fails again with the same
    // words must put it back, not leave it standing silently (add, rename and delete clear it too).
    onMutate: () => setError(null),
    onSuccess: (data: PostingSchedule) => {
      setError(null);
      queryClient.setQueryData(['posting-schedule', tenantKey], data);
      // Re-hydrate the form from the saved result so the editor never diverges from the server
      // (e.g. an explicit empty save stays empty rather than showing stale local rows; a category
      // another admin deleted meanwhile goes, one they added arrives).
      const saved = hydrateDraft(data);
      setDraft(saved);
      setSelected((s) => (s !== null && !saved.categories.some((c) => c.id === s) ? null : s));
      // New/removed slots change every post's open-slot suggestions.
      void queryClient.invalidateQueries({ queryKey: ['schedule-suggestions', tenantKey] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Could not save'),
  });

  const createCategory = useMutation({
    mutationFn: (dto: CreateSchedulingCategoryDto) => api.createSchedulingCategory(dto),
  });
  const updateCategory = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateSchedulingCategoryDto }) =>
      api.updateSchedulingCategory(id, dto),
  });
  const deleteCategory = useMutation({
    mutationFn: (id: string) => api.deleteSchedulingCategory(id),
  });
  // A save re-hydrates the draft from its answer: a category written meanwhile could be dropped from
  // the editor by it, so the two never overlap.
  const categoryBusy = createCategory.isPending || updateCategory.isPending || deleteCategory.isPending;

  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const tabId = (id: ScheduleId) => `${baseId}-tab-${id ?? 'general'}`;
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  // A tab to focus once it is drawn: the new category after adding one, General after a delete.
  const [focusTab, setFocusTab] = useState<{ id: ScheduleId } | null>(null);
  useEffect(() => {
    if (!focusTab) return;
    tabRefs.current.get(tabKey(focusTab.id))?.focus();
    setFocusTab(null);
  }, [focusTab]);

  const add = async (name: string, color: string) => {
    setError(null);
    const created = await createCategory.mutateAsync({ name, color });
    setDraft((d) => d && addCategory(d, created));
    setSelected(created.id);
    // Clear a search, so the new row is in the list for the focus to land on.
    setQuery('');
    setFocusTab({ id: created.id });
    void invalidateCategoryViews(queryClient, tenantKey);
  };

  const rename = async (category: DraftCategory, name: string, color: string) => {
    setError(null);
    // Only what changed: a colour change never puts back a name another admin has just changed.
    const dto: UpdateSchedulingCategoryDto = {
      ...(name !== category.name ? { name } : {}),
      ...(color !== category.color ? { color } : {}),
    };
    if (dto.name === undefined && dto.color === undefined) return;
    const updated = await updateCategory.mutateAsync({ id: category.id, dto });
    setDraft((d) => d && renameCategory(d, updated));
    void invalidateCategoryViews(queryClient, tenantKey);
  };

  const remove = async (category: DraftCategory): Promise<boolean> => {
    setError(null);
    try {
      const { postCount } = await api.getSchedulingCategoryUsage(category.id);
      const ok = await confirm({
        title: `Delete "${category.name}"?`,
        message: deleteMessage(postCount),
        confirmLabel: 'Delete category',
        danger: true,
      });
      if (!ok) return false;
      await deleteCategory.mutateAsync(category.id);
    } catch (err) {
      // A 404: another admin has deleted it already, and the page catches up below. Anything else
      // leaves the category where it is.
      if (!(err instanceof ApiRequestError && err.status === 404)) {
        setError(actionErrorText(err, 'Could not delete the category.'));
        return false;
      }
    }
    setDraft((d) => d && removeCategory(d, category.id));
    setSelected(null);
    // The confirmation hands no focus back, and the button that opened it is gone: General takes it.
    setFocusTab({ id: null });
    void invalidateCategoryViews(queryClient, tenantKey);
    return true;
  };

  if (!draft) {
    // Only before the first answer: once the editor holds a draft, a failed refresh must not hide it
    // (and its unsaved times) behind an error.
    if (scheduleQ.isError) {
      return (
        <ErrorState
          message={
            scheduleQ.error instanceof ApiRequestError
              ? scheduleQ.error.message
              : 'Could not load the posting schedule.'
          }
          onRetry={() => void scheduleQ.refetch()}
        />
      );
    }
    return <LoadingState label="Loading posting schedule…" />;
  }

  // The selected schedule, as the draft has it (a category it no longer has shows General).
  const category = selected === null ? null : (draft.categories.find((c) => c.id === selected) ?? null);
  const current: ScheduleId = category ? category.id : null;
  // A search can hide the selected row; the panel is then named by itself instead of by its tab.
  const tabShown = current === null || shownCategories(draft.categories, query).some((c) => c.id === current);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-ink-muted">
          The recurring times the posting queue fills. Suggested Slots and Next Available /
          Prioritize / Pick a Month all draw from these.
        </p>
        {isAdmin && (
          <Button
            loading={save.isPending}
            disabled={categoryBusy}
            onClick={() => save.mutate(toDto(draft))}
            className="shrink-0"
          >
            Save schedule
          </Button>
        )}
      </div>

      {readOnly && (
        <div className="rounded-lg border border-line bg-page px-4 py-2.5 text-sm text-ink-label">
          The posting schedule is managed by a workspace admin. You can view it here.
        </div>
      )}

      <Card>
        <CardHeader title="Timezone" description="All posting times below are local to this timezone." />
        <CardBody>
          <Select
            value={timezone}
            disabled={readOnly}
            onChange={(e) => {
              const value = e.target.value;
              setDraft((d) => d && { ...d, timezone: value });
            }}
            className="max-w-sm"
            aria-label="Timezone"
          >
            {tzOptions.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Weekly slots"
          description="Add the times you want to post each day. Each category has its own schedule."
        />
        {/* Above both columns: Save writes every schedule, not the one on show. */}
        {(save.isSuccess || error) && (
          <div className="border-b border-line-soft px-5 py-3">
            {save.isSuccess && !error && <p className="text-sm text-success dark:text-success-dark">Schedule saved.</p>}
            {/* An alert: a failed save or delete is read out, wherever the focus is. */}
            {error && (
              <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
                {error}
              </p>
            )}
          </div>
        )}
        {/* Two columns once the card itself is 46rem wide (sidebar 16.25rem + the day cards' ~27rem):
            a container query, so the app sidebar's arrival at 1024px never squeezes the day cards.
            Narrower, the schedules stack above the selected one at full width. */}
        <div className="[container-type:inline-size]">
          <div className="flex flex-col [@container(min-width:46rem)]:flex-row">
            <ScheduleSidebar
              generalCount={enabledCount(draft.general)}
              categories={draft.categories.map((c) => ({
                id: c.id,
                name: c.name,
                color: c.color,
                count: enabledCount(c.slots),
              }))}
              selected={current}
              onSelect={setSelected}
              query={query}
              onQueryChange={setQuery}
              tabId={tabId}
              panelId={panelId}
              tabRefs={tabRefs}
              canEdit={isAdmin}
              addDisabled={save.isPending}
              onAdd={add}
              className="w-full border-b border-line px-5 py-4 [@container(min-width:46rem)]:w-[16.25rem] [@container(min-width:46rem)]:shrink-0 [@container(min-width:46rem)]:border-b-0 [@container(min-width:46rem)]:border-r"
            />
            <SchedulePanel
              key={tabKey(current)}
              id={panelId}
              labelledBy={tabShown ? tabId(current) : undefined}
              category={category}
              categories={draft.categories}
              slots={slotsOf(draft, current)}
              readOnly={readOnly}
              showIntroNote={draft.categories.length === 0}
              actionsDisabled={save.isPending}
              addSlot={(day) => setDraft((d) => d && addSlot(d, current, day))}
              removeSlot={(key) => setDraft((d) => d && removeSlot(d, current, key))}
              patchSlot={(key, patch) => setDraft((d) => d && patchSlot(d, current, key, patch))}
              onRename={(name, color) => (category ? rename(category, name, color) : Promise.resolve())}
              onDelete={() => (category ? remove(category) : Promise.resolve(false))}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
